import type { DerivedPageAssetSlot, SinglePageAssetSlot } from "~/lib/assets/slots";
import { IMAGE_VARIANTS, type ImageVariant } from "~/lib/images/variants";

import { AssetPlaceholder, warnEmptyAssetSlot } from "./AssetPlaceholder";
import { Image } from "./image";
import { usePageAsset } from "./PageAssetsProvider";

/**
 * T219 — render a managed page image by registry slot in one line.
 * `alt` always comes from the asset record (ES required at publish, R-T218-4);
 * an empty or non-image slot renders the documented placeholder instead.
 */
export type SlotImageProps = {
  slot: SinglePageAssetSlot | DerivedPageAssetSlot;
  variant: ImageVariant;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  /** Responsive widths for the two uncropped hero variants only. */
  responsive?: boolean;
  sizes?: string;
  className?: string;
};

export function SlotImage({ slot, variant, loading, fetchPriority, responsive, sizes, className }: SlotImageProps) {
  const asset = usePageAsset(slot);
  const src = asset?.kind === "image" ? asset.imageUrls?.[variant] : undefined;

  if (!asset || !src) {
    warnEmptyAssetSlot(slot, asset ? "no renderable image for this slot" : "no published asset");
    return <AssetPlaceholder variant={variant} className={className} />;
  }

  const mobile = asset.imageUrls?.heroMobile;
  const desktop = asset.imageUrls?.heroDesktop;
  const srcSet = responsive && (variant === "heroDesktop" || variant === "heroMobile") && mobile && desktop
    ? `${mobile} ${IMAGE_VARIANTS.heroMobile.width}w, ${desktop} ${IMAGE_VARIANTS.heroDesktop.width}w`
    : undefined;
  return <Image src={src} variant={variant} alt={asset.alt} loading={loading}
    fetchPriority={fetchPriority} srcSet={srcSet} sizes={sizes} className={className} />;
}
